// Delete without a dialog (after Bencho's inline confirm): pressing Delete turns the button into "Deleted · Undo"
// with a fuse burning down, and dims its row ([data-undo-row]). The delete itself runs when the fuse ends, so Undo
// simply cancels it. Following a link while a fuse burns runs the delete first and then follows the link; closing
// the tab sends it with keepalive. Self-registers `uiDeleteUndo` on alpine:init; x-ui.delete-undo renders it.

const pending = new Set();
let keepalive = false;

/**
 * @param {string} run  Alpine expression to evaluate on commit, e.g. "$wire.archive(9)" or "revokeNow(t)".
 * @param {number} wait milliseconds before the delete runs
 */
export function uiDeleteUndo(run, wait = 4000) {
    return {
        state: 'idle',
        timer: null,
        row() { return this.$root.closest('[data-undo-row]'); },
        press() {
            if (this.state !== 'idle') return;
            this.state = 'pending';
            this.row()?.setAttribute('data-deleting', '');
            this.timer = setTimeout(() => this.commit(), wait);
            pending.add(this);
            this.$nextTick(() => this.$root.querySelector('.ui-du-undo')?.focus({ preventScroll: true }));
        },
        undo() {
            if (this.state !== 'pending') return;
            clearTimeout(this.timer);
            pending.delete(this);
            this.state = 'idle';
            this.row()?.removeAttribute('data-deleting');
            this.$nextTick(() => this.$root.querySelector('.ui-du-go')?.focus({ preventScroll: true }));
        },
        commit() {
            if (this.state !== 'pending') return;
            clearTimeout(this.timer);
            pending.delete(this);
            this.state = 'busy';
            const done = Promise.resolve(window.Alpine.evaluate(this.$root, run));
            done.catch(() => {
                this.state = 'idle';
                this.row()?.removeAttribute('data-deleting');
            });
            return done;
        },
    };
}

function flush() {
    if (!pending.size) return Promise.resolve();
    keepalive = true;
    const all = Promise.allSettled([...pending].map((c) => c.commit()));
    setTimeout(() => { keepalive = false; }, 1000);
    return all;
}

// A link followed mid-fuse waits for the delete, so it isn't lost with the page.
function holdLinks(e) {
    if (!pending.size || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
    if (!a || a.target === '_blank' || a.hasAttribute('download') || a.getAttribute('href').startsWith('#')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const go = () => { window.location.href = a.href; };
    flush().then(go, go);
}

if (typeof window !== 'undefined' && !window.__uiUndo) {
    window.__uiUndo = true;
    document.addEventListener('alpine:init', () => window.Alpine.data('uiDeleteUndo', uiDeleteUndo));
    document.addEventListener('livewire:init', () => {
        window.Livewire.hook('request', ({ options }) => { if (keepalive) options.keepalive = true; });
    });
    document.addEventListener('click', holdLinks, true);
    window.addEventListener('pagehide', () => { flush(); });
}
